/**
 * S-15 Matches & conversations list (V3a chat-fuse) — v3 "AFTER-DARK ARCADE".
 *
 * Romance register: warm, tactile, filmic — layered cards, editorial-serif
 * asides, neon only where a verb needs it. Fresh-matches rail (unopened, live
 * fuse countdowns + a distinct YOUR MOVE / THEIR MOVE treatment for the
 * women-message-first state), the conversation list (presence per #51
 * reciprocity, unread badges with bloom, previews with fold/unsend stubs),
 * and the collapsed Expired morgue — SM-13b's Run It Back entry point
 * (expiry hides, never deletes).
 *
 * SPLIT LISTS: the two match origins live in SEPARATE, distinctly-headed
 * groups — "MATCHES" (like-family: MatchView.type classic/roulette) and
 * "REJECT-MATCHES" (type reject, the flagship) — never interleaved. Each
 * group keeps the full fresh-rail / talking-list / expired-morgue grouping
 * and every behavior (openers, fuse timers, women-first move tags, RIB).
 * The Reject-Matches group renders only when it holds at least one match.
 *
 * Identical /matches + WS wiring as v2; every class the specs may touch is
 * preserved (.rail-item--reject, .m-row__reject-badge, .m-row__reject-mark,
 * .matches__expired*, …).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { formatCopy, resolveCopy } from "@humble/shared";
import { AnimatePresence, motion } from "framer-motion";
import {
  Badge,
  Card,
  IconBrokenHeartFill,
  Mascot,
  rise,
  Skeleton,
  stagger,
  VerifiedBadge,
} from "../../design/index.js";
import { api } from "../../lib/api.js";
import { ws } from "../../lib/ws.js";
import { fuseRemainingMs, formatRemaining, useNow } from "./use-now.js";
import { type MatchViewWire, type PresenceBucket } from "./types.js";
import "./matches.css";

const PRESENCE_LABEL: Record<Exclude<PresenceBucket, "none">, string> = {
  online: "chat.presence_online",
  today: "chat.presence_today",
  this_week: "chat.presence_week",
};

export function PresenceDot({ bucket }: { bucket: PresenceBucket }) {
  // #51: past the third bucket NOTHING renders — the absence IS the state.
  if (bucket === "none") return null;
  return (
    <span
      className={`presence presence--${bucket}`}
      title={resolveCopy(PRESENCE_LABEL[bucket])}
      aria-label={resolveCopy(PRESENCE_LABEL[bucket])}
    />
  );
}

export function Avatar({
  match,
  size = 48,
}: {
  match: MatchViewWire;
  size?: number;
}) {
  const [broken, setBroken] = useState(false);
  // Server-signed viewer-bound URL (plain <img> renderable — no bearer needed).
  const url = match.counterpart.avatar_url;
  const name = match.counterpart.display_name ?? "?";
  return (
    <span
      className={`m-avatar ${match.counterpart.purged ? "m-avatar--tomb" : ""}`}
      style={{ width: size, height: size }}
    >
      {url && !broken ? (
        <img src={url} alt="" onError={() => setBroken(true)} />
      ) : (
        <span className="m-avatar__initial">{name.slice(0, 1).toUpperCase()}</span>
      )}
    </span>
  );
}

function previewText(m: MatchViewWire): string {
  const lm = m.last_message;
  if (!lm)
    return m.type === "reject"
      ? (resolveCopy("matches.preview_none_reject") ?? "Mutual rejection. Discuss.")
      : (resolveCopy("matches.preview_none") ?? "Say something.");
  if (lm.unsent) return resolveCopy("chat.message_removed") ?? "Message removed.";
  if (lm.folded) return resolveCopy("chat.hidden_by_filters") ?? "Hidden by your filters.";
  if (lm.kind === "gif") return resolveCopy("matches.preview_gif") ?? "GIF";
  if (lm.kind === "image") return resolveCopy("matches.preview_photo") ?? "Photo";
  if (lm.kind === "voice") return resolveCopy("matches.preview_voice") ?? "Voice note";
  return lm.body ?? "";
}

function FuseChip({ match, now }: { match: MatchViewWire; now: number }) {
  if (match.fuse.state !== "fuse_running") return null;
  const remaining = fuseRemainingMs(match.fuse, now);
  if (remaining === null) return null;
  const urgent = !match.fuse.frozen && remaining < 6 * 3600_000;
  return (
    <span
      className={`fuse-chip type-numeric ${urgent ? "fuse-chip--urgent" : ""} ${match.fuse.frozen ? "fuse-chip--frozen" : ""}`}
    >
      {match.fuse.frozen ? "⏸ " : "⏱ "}
      {formatRemaining(remaining)}
    </span>
  );
}

/** The women-message-first treatment: whose move is it on an unopened match.
 *  "you" burns accent; "them" waits, dim and patient (©match.wait_your_turn). */
function MoveTag({ whose }: { whose: "you" | "them" | "both" | null }) {
  if (whose !== "you" && whose !== "them") return null;
  const label = resolveCopy(whose === "you" ? "matches.move_you" : "matches.move_them");
  const hint = resolveCopy(whose === "you" ? "matches.move_you_sub" : "match.wait_your_turn");
  return (
    <span className={`move-tag move-tag--${whose}`} title={hint}>
      {label}
    </span>
  );
}

function MatchesSkeleton() {
  return (
    <div className="matches matches--loading" aria-busy="true">
      <header className="matches__head">
        <h1 className="matches__title type-display-3">
          {resolveCopy("matches.conversations_title") ?? "Conversations"}
        </h1>
      </header>
      <div className="matches__rail matches__rail--skel" aria-hidden="true">
        {Array.from({ length: 4 }, (_, i) => (
          <span key={i} className="rail-item rail-item--skel">
            <Skeleton width={64} height={64} radius="50%" />
            <Skeleton width={52} height={10} />
          </span>
        ))}
      </div>
      <div className="matches__skel-rows" aria-hidden="true">
        {Array.from({ length: 5 }, (_, i) => (
          <div key={i} className="m-row m-row--skel">
            <Skeleton width={48} height={48} radius="50%" />
            <span className="m-row__main">
              <Skeleton width={`${46 - i * 4}%`} height={13} />
              <Skeleton width={`${72 - i * 6}%`} height={11} />
            </span>
          </div>
        ))}
      </div>
      <Mascot mood="loading" quip={resolveCopy("matches.loading_quip") ?? ""} />
    </div>
  );
}

/** One origin-family group ("like" = classic+roulette, "reject" = mutual
 *  rejection): its own header, then the preserved fresh-rail → talking-list →
 *  expired-morgue grouping scoped to that family's matches. */
function MatchFamilyGroup({
  family,
  matches,
  now,
  typingIn,
  expiredOpen,
  onToggleExpired,
  onOpen,
}: {
  family: "like" | "reject";
  matches: MatchViewWire[];
  now: number;
  typingIn: Record<string, boolean>;
  expiredOpen: boolean;
  onToggleExpired: () => void;
  onOpen: (matchId: string) => void;
}) {
  const active = matches.filter((m) => m.fuse.state !== "expired");
  const expired = matches.filter((m) => m.fuse.state === "expired");
  const fresh = active.filter((m) => m.last_seq === 0);
  const talking = active.filter((m) => m.last_seq > 0);
  const title =
    resolveCopy(family === "reject" ? "matches.section_reject" : "matches.section_like") ??
    (family === "reject" ? "REJECT-MATCHES" : "MATCHES");
  const sub = resolveCopy(
    family === "reject" ? "matches.section_reject_sub" : "matches.section_like_sub",
  );

  return (
    <section className={`matches__group matches__group--${family}`} aria-label={title}>
      <header className="matches__group-head">
        <h2 className="matches__group-title h-caps">
          {family === "reject" && (
            <IconBrokenHeartFill width={16} height={16} aria-hidden="true" />
          )}
          {title}
        </h2>
        {sub && <p className="matches__group-sub type-editorial">{sub}</p>}
      </header>

      {fresh.length > 0 && (
        <section aria-label={resolveCopy("matches.rail_title")}>
          <h3 className="matches__section-title h-caps">{resolveCopy("matches.rail_title")}</h3>
          <motion.div className="matches__rail" variants={stagger} initial="initial" animate="enter">
            {fresh.map((m) => {
              const remaining = fuseRemainingMs(m.fuse, now);
              const pulse = !m.fuse.frozen && remaining !== null && remaining < 6 * 3600_000;
              const whose = m.fuse.whose_move;
              return (
                <motion.button
                  key={m.match_id}
                  variants={rise}
                  whileTap={{ scale: 0.95 }}
                  className={[
                    "rail-item",
                    pulse ? "rail-item--pulse" : "",
                    whose === "you" ? "rail-item--your-move" : "",
                    whose === "them" ? "rail-item--their-move" : "",
                    m.type === "reject" ? "rail-item--reject" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  onClick={() => onOpen(m.match_id)}
                >
                  <span className="rail-item__frame">
                    <Avatar match={m} size={64} />
                    {m.type === "reject" && (
                      <span className="rail-item__reject" title={resolveCopy("match.long_fuse_badge")}>
                        <IconBrokenHeartFill width={15} height={15} />
                      </span>
                    )}
                  </span>
                  <span className="rail-item__name">{m.counterpart.display_name ?? "…"}</span>
                  <MoveTag whose={whose} />
                  <FuseChip match={m} now={now} />
                </motion.button>
              );
            })}
          </motion.div>
        </section>
      )}

      {talking.length > 0 && (
        <motion.ul
          className="matches__list"
          aria-label={family === "reject" ? "Reject-Match conversations" : "Conversations"}
          variants={stagger}
          initial="initial"
          animate="enter"
        >
          {talking.map((m) => (
            <motion.li key={m.match_id} variants={rise}>
              <button className="m-row" onClick={() => onOpen(m.match_id)}>
                <span className="m-row__avatar-frame">
                  <Avatar match={m} />
                  <PresenceDot bucket={m.counterpart_presence} />
                </span>
                <span className="m-row__main">
                  <span className="m-row__top">
                    <span className="m-row__name">
                      {m.counterpart.purged
                        ? (resolveCopy("chat.tombstone") ?? "Deleted account")
                        : (m.counterpart.display_name ?? "…")}
                      {m.counterpart.verified && <VerifiedBadge />}
                      {m.type === "reject" && <Badge className="m-row__reject-badge">REJECT-MATCH</Badge>}
                    </span>
                  </span>
                  <span
                    className={`m-row__preview text-dim ${typingIn[m.match_id] ? "m-row__preview--typing" : ""}`}
                  >
                    {typingIn[m.match_id] ? (
                      <>
                        <span className="typing-dots" aria-hidden="true">
                          <i />
                          <i />
                          <i />
                        </span>
                        {resolveCopy("chat.typing") ?? "typing…"}
                      </>
                    ) : (
                      previewText(m)
                    )}
                  </span>
                </span>
                <span className="m-row__meta">
                  <FuseChip match={m} now={now} />
                  {m.unread > 0 && (
                    <motion.span
                      key={m.unread}
                      className="m-row__unread type-numeric"
                      initial={{ scale: 0.5, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ type: "spring", stiffness: 480, damping: 22 }}
                    >
                      {m.unread}
                    </motion.span>
                  )}
                </span>
              </button>
            </motion.li>
          ))}
        </motion.ul>
      )}

      <section className="matches__expired">
        <button
          className="matches__expired-toggle"
          onClick={onToggleExpired}
          aria-expanded={expiredOpen}
        >
          <span className="h-caps">{resolveCopy("matches.expired_title")}</span>
          <span className="text-faint type-numeric">
            {expired.length} {expiredOpen ? "▾" : "▸"}
          </span>
        </button>
        <AnimatePresence initial={false}>
          {expiredOpen && (
            <motion.div
              className="matches__expired-body"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
            >
              {expired.length === 0 ? (
                <p className="text-faint matches__expired-empty type-editorial">
                  {resolveCopy("matches.expired_empty")}
                </p>
              ) : (
                <ul className="matches__list">
                  {expired.map((m) => (
                    <li key={m.match_id}>
                      <button className="m-row m-row--expired" onClick={() => onOpen(m.match_id)}>
                        <Avatar match={m} />
                        <span className="m-row__main">
                          <span className="m-row__name">
                            {m.counterpart.display_name ?? "…"}
                            {m.type === "reject" && (
                              <span className="m-row__reject-mark" title="Reject-Match">
                                <IconBrokenHeartFill width={13} height={13} />
                              </span>
                            )}
                          </span>
                          <span className="m-row__preview text-faint">
                            {formatCopy(resolveCopy("matches.expired_ago") ?? "expired {n}d ago", {
                              n: m.matched_at
                                ? Math.max(0, Math.floor((now - new Date(m.matched_at).getTime()) / 86_400_000))
                                : 0,
                            })}
                          </span>
                        </span>
                        {(m.rib.offered_by_me || m.rib.offered_by_them) && (
                          <Badge className="m-row__rib-badge">RUN IT BACK</Badge>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </section>
    </section>
  );
}

export function MatchesScreen() {
  const [matches, setMatches] = useState<MatchViewWire[] | null>(null);
  const [query, setQuery] = useState("");
  const [expiredOpen, setExpiredOpen] = useState<{ like: boolean; reject: boolean }>({
    like: false,
    reject: false,
  });
  const [typingIn, setTypingIn] = useState<Record<string, boolean>>({});
  const navigate = useNavigate();
  const now = useNow(1000);

  const refresh = useCallback(async () => {
    try {
      const res = (await api.get("/matches")) as unknown as { matches: MatchViewWire[] };
      setMatches(res.matches);
    } catch {
      setMatches((prev) => prev ?? []);
    }
  }, []);

  useEffect(() => {
    void refresh();
    ws.ensureConnected();
    const offs = [
      ws.on("match.created", () => void refresh()),
      ws.on("match.expired", () => void refresh()),
      ws.on("match.extended", () => void refresh()),
      ws.on("match.unmatched", () => void refresh()),
      ws.on("rib.offered", () => void refresh()),
      ws.on("rib.reopened", () => void refresh()),
      ws.on("msg.new", () => void refresh()),
      ws.on("msg.unsent", () => void refresh()),
      ws.on("presence.update", () => void refresh()),
      ws.on("typing", (p) => {
        setTypingIn((prev) => ({ ...prev, [p.match_id]: p.on }));
        if (p.on) window.setTimeout(() => setTypingIn((prev) => ({ ...prev, [p.match_id]: false })), 6000);
      }),
    ];
    return () => offs.forEach((off) => off());
  }, [refresh]);

  const filtered = useMemo(() => {
    if (!matches) return null;
    const q = query.trim().toLowerCase();
    if (!q) return matches;
    return matches.filter((m) => (m.counterpart.display_name ?? "").toLowerCase().includes(q));
  }, [matches, query]);

  if (filtered === null) {
    return <MatchesSkeleton />;
  }

  // THE SPLIT: reject-matches live in their own group, never interleaved
  // with the like-family (classic + roulette).
  const likeMatches = filtered.filter((m) => m.type !== "reject");
  const rejectMatches = filtered.filter((m) => m.type === "reject");
  const totallyEmpty = filtered.length === 0;
  const onOpen = (matchId: string) => navigate(`/matches/${matchId}`);

  return (
    <div className="matches">
      <header className="matches__head">
        <h1 className="matches__title type-display-3">
          {resolveCopy("matches.conversations_title") ?? "Conversations"}
        </h1>
        <input
          className="matches__search"
          type="search"
          placeholder={resolveCopy("matches.search_placeholder")}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search matches"
        />
      </header>

      {totallyEmpty && (
        <>
          <Card padding="lg" className="matches__empty">
            <Mascot mood="ko" size={40} />
            <p className="matches__empty-kicker type-editorial">
              {resolveCopy("matches.empty_kicker")}
            </p>
            <p className="text-dim matches__empty-body">{resolveCopy("matches.empty")}</p>
            <Link to="/deck" className="btn btn--primary">
              {resolveCopy("matches.deck_cta") ?? "To the deck"}
            </Link>
          </Card>
          {/* the morgue toggle stays reachable even with nothing anywhere */}
          <section className="matches__expired">
            <button
              className="matches__expired-toggle"
              onClick={() => setExpiredOpen((v) => ({ ...v, like: !v.like }))}
              aria-expanded={expiredOpen.like}
            >
              <span className="h-caps">{resolveCopy("matches.expired_title")}</span>
              <span className="text-faint type-numeric">0 {expiredOpen.like ? "▾" : "▸"}</span>
            </button>
            <AnimatePresence initial={false}>
              {expiredOpen.like && (
                <motion.div
                  className="matches__expired-body"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
                >
                  <p className="text-faint matches__expired-empty type-editorial">
                    {resolveCopy("matches.expired_empty")}
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </section>
        </>
      )}

      {likeMatches.length > 0 && (
        <MatchFamilyGroup
          family="like"
          matches={likeMatches}
          now={now}
          typingIn={typingIn}
          expiredOpen={expiredOpen.like}
          onToggleExpired={() => setExpiredOpen((v) => ({ ...v, like: !v.like }))}
          onOpen={onOpen}
        />
      )}

      {rejectMatches.length > 0 && (
        <MatchFamilyGroup
          family="reject"
          matches={rejectMatches}
          now={now}
          typingIn={typingIn}
          expiredOpen={expiredOpen.reject}
          onToggleExpired={() => setExpiredOpen((v) => ({ ...v, reject: !v.reject }))}
          onOpen={onOpen}
        />
      )}
    </div>
  );
}
